/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useEffect, useState } from 'react';

import {
  BLACK,
  WHITE,
  GRAY_DARK,
  PURPLE_FEEK,
  PURPLE_LIGHT,
} from 'styles/colors';
import APIClient from 'APIClient';
import Text from 'components/Text';
import CDMX from 'assets/images/Cdmx.png';
import handleError from 'utils/handleError';
import { MEDIA_QUERIES } from 'styles/constants';
import Saltillo from 'assets/images/Saltillo.png';
import Monterrey from 'assets/images/Monterrey.png';
import { AvatarsCabinet } from 'components/AvatarsCabinet';

const styles = css`
  background-color: ${PURPLE_LIGHT};
  padding: 150px 100px;

  .mb-32 {
    margin-bottom: 32px;
  }

  .citiesList {
    margin-top: 84px;
    display: flex;
    gap: 32px;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .city {
    display: flex;
    flex-direction: column;
    background-color: ${WHITE};
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
    width: 100%;
    max-width: 380px;
  }

  .city-footer {
    padding: 16px 16px;
  }

  .cityName {
    margin-bottom: 8px;
  }

  .tag-list {
    margin-bottom: 24px;
  }

  .tag {
    display: inline-block;
    border: 1px solid ${BLACK};
    padding: 4px 12px;
    border-radius: 16px;
    margin-right: 8px;
    margin-bottom: 8px;
  }

  .avatars {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .feekspotsCount {
    width: 40px;
    height: 40px;
    display: flex;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    border: 1px solid ${BLACK};
  }

  .cityImage {
    width: 100%;
    height: auto;
  }

  ${MEDIA_QUERIES.tablet} {
    padding: 100px 50px;
  }

  ${MEDIA_QUERIES.mobile} {
    padding: 50px 20px;

    .citiesList {
      flex-direction: column;
      gap: 24px;
    }

    .city {
      max-width: 100%;
    }

    .cityName {
      font-size: 24px;
    }

    .tag {
      font-size: 12px;
      padding: 2px 8px;
    }

    .avatars {
      flex-direction: column;
      gap: 8px;
      align-items: flex-start;
    }

    .feekspotsCount {
      align-self: flex-end;
    }
  }
`;

const citiesImages = {
  Monterrey: Monterrey,
  Saltillo: Saltillo,
  CDMX: CDMX,
};

const FeekspotsCities = () => {
  const [cities, setCities] = useState(Array(3));

  useEffect(() => {
    APIClient.getCitiesToShow()
      .then(({ data }) =>
        setCities(
          data
            .slice(0, 3)
            .map(
              ({
                location: { city: location },
                customers: feekspots,
                total,
                categories,
              }) => ({
                name: location,
                categories: categories || [
                  'Restaurante',
                  'Bar',
                  'Coffee',
                  'Snacks',
                  'Shopping',
                  'Beauty',
                ],
                feekspotsAvatars:
                  feekspots.slice(0, 7).map(({ image }) => image) ||
                  [],
                feekspotsCount: total,
              }),
            ),
        ),
      )
      .catch(handleError);
  }, []);

  return (
    <div css={styles} id="cities">
      <Text
        fontSize={48}
        fontWeight={600}
        color={PURPLE_FEEK}
        className="mb-32"
        align="center"
      >
        ¡Presentes en tu ciudad favorita!
      </Text>
      <Text fontSize={24} color={GRAY_DARK} align="center">
        <b>Cientos de Feekspots</b> en 3 ciudades TOP del país.
        Monterrey, Saltillo y Ciudad de México.
      </Text>

      <ul className="citiesList">
        {cities
          .slice(0, 3)
          .map(
            (
              { name, categories, feekspotsAvatars, feekspotsCount },
              index,
            ) => (
              <li key={index} className="city">
                <img
                  src={citiesImages?.[name] || Monterrey}
                  alt={name}
                  className="cityImage"
                />
                <div className="city-footer">
                  <Text
                    fontSize={28}
                    fontWeight={600}
                    color={PURPLE_FEEK}
                    className="cityName"
                  >
                    {name}
                  </Text>

                  <ul className="tag-list">
                    {categories?.map((category, index) => (
                      <Text
                        className="tag"
                        key={index}
                        fontSize={14}
                        color={BLACK}
                        align="center"
                      >
                        {category}
                      </Text>
                    ))}
                  </ul>

                  <div className="avatars">
                    <AvatarsCabinet
                      avatarsSize={40}
                      overlap={22}
                      avatars={feekspotsAvatars?.slice(0, 7)}
                    />

                    <Text
                      type="inline"
                      fontSize={16}
                      fontWeight={600}
                      className="feekspotsCount"
                    >
                      +{feekspotsCount || feekspotsAvatars.length}
                    </Text>
                  </div>
                </div>
              </li>
            ),
          )}
      </ul>
    </div>
  );
};

export default FeekspotsCities;
